<template>
  <div class="icp-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-content">
        <p class="hero-label">备案服务</p>
        <h1 class="hero-title">ICP备案一站式服务</h1>
        <p class="hero-subtitle">免费代备案 · 全程指导 · 快速通过</p>
      </div>
    </section>

    <!-- Main Content -->
    <div class="page-container">
      <div class="two-column-layout">
        <!-- Left: Process Timeline -->
        <div class="process-section">
          <h3 class="section-title">备案流程</h3>
          <div class="process-timeline">
            <div v-for="(step, index) in processSteps" :key="step.step" class="process-step">
              <div class="step-indicator">
                <div class="step-circle" :class="{ completed: index === processSteps.length - 1 }">
                  {{ index === processSteps.length - 1 ? '✓' : step.step }}
                </div>
                <div v-if="index < processSteps.length - 1" class="step-line"></div>
              </div>
              <div class="step-info">
                <h4 class="step-title">{{ step.title }}</h4>
                <p class="step-desc">{{ step.desc }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Filing Form -->
        <div class="filing-form-section">
          <h3 class="form-title">开始备案</h3>
          <p class="form-subtitle"> 购买任一建站产品即可享受免费备案服务 </p>
          <div class="filing-form">
            <input v-model="contactName" type="text" placeholder="联系人姓名" class="form-input" />
            <input v-model="contactPhone" type="tel" placeholder="联系电话" class="form-input" />
            <input
              v-model="domain"
              type="text"
              placeholder="网站域名（如：www.example.com）"
              class="form-input"
            />
            <input v-model="orgName" type="text" placeholder="主办单位名称" class="form-input" />
            <button class="submit-btn" @click="submitFiling"> 提交备案申请 </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { getSubmitErrorMessage, submitPortalForm } from '../usePortalSubmission'

defineOptions({ name: 'IcpPage' })

const domain = ref<string>('')
const orgName = ref<string>('')
const contactName = ref('')
const contactPhone = ref('')

const processSteps = [
  {
    step: 1,
    title: '填写备案信息',
    desc: '提交主体信息、网站信息、负责人信息'
  },
  {
    step: 2,
    title: '上传证件资料',
    desc: '营业执照、法人身份证、域名证书等'
  },
  {
    step: 3,
    title: '初审与提交',
    desc: '平台初审 → 通信管理局审核'
  },
  {
    step: 4,
    title: '备案完成',
    desc: '获取ICP备案号，网站正式上线'
  }
]

async function submitFiling() {
  if (!contactName.value.trim() || !contactPhone.value.trim()) {
    ElMessage.warning('请输入联系人和联系电话')
    return
  }
  if (!domain.value.trim()) {
    ElMessage.warning('请输入网站域名')
    return
  }
  if (!orgName.value.trim()) {
    ElMessage.warning('请输入主办单位名称')
    return
  }
  try {
    await submitPortalForm({
      username: contactName.value,
      companyName: orgName.value,
      tel: contactPhone.value,
      productName: domain.value,
      content: `备案域名：${domain.value}`,
      type: 'ICP_FILING'
    })
    ElMessage.success('备案申请已提交，工作人员将在1个工作日内联系您')
    contactName.value = ''
    contactPhone.value = ''
    domain.value = ''
    orgName.value = ''
  } catch (error) {
    ElMessage.error(getSubmitErrorMessage(error))
  }
}
</script>

<style lang="scss" scoped>
.icp-page {
  // Hero Banner
  .hero-banner {
    height: 200px;
    background: linear-gradient(135deg, #001b5e, #0044cc);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #fff;
  }

  .hero-content {
    padding: 0 24px;
  }

  .hero-label {
    font-size: 14px;
    opacity: 0.6;
    letter-spacing: 4px;
    margin: 0 0 8px;
  }

  .hero-title {
    font-size: 36px;
    font-weight: 900;
    margin: 0 0 8px;
  }

  .hero-subtitle {
    font-size: 15px;
    opacity: 0.7;
    margin: 0;
  }

  // Page Container
  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 24px;
  }

  // Two Column Layout
  .two-column-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  // Process Section
  .section-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 20px;
    color: #111827;
  }

  .process-timeline {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .process-step {
    display: flex;
    gap: 20px;
  }

  .step-indicator {
    position: relative;
  }

  .step-circle {
    width: 40px;
    height: 40px;
    background: #0066ff;
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    flex-shrink: 0;
    z-index: 2;
    position: relative;

    &.completed {
      background: #00b96b;
    }
  }

  .step-line {
    position: absolute;
    top: 40px;
    left: 19px;
    width: 2px;
    height: calc(100% + 16px);
    background: #ebf5ff;
  }

  .step-info {
    padding-top: 8px;
  }

  .step-title {
    font-weight: 600;
    margin: 0 0 4px;
    color: #111827;
  }

  .step-desc {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  // Filing Form Section
  .filing-form-section {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
  }

  .form-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 8px;
    color: #111827;
  }

  .form-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 20px;
  }

  .filing-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .form-input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s ease;
    box-sizing: border-box;

    &:focus {
      border-color: #0066ff;
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  .submit-btn {
    width: 100%;
    height: 44px;
    background: #0066ff;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #0044cc;
    }
  }
}
</style>
